import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FormSelect } from '../../components/FormField'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { apiErrorMessage } from '../../lib/errors'
import { businessDayOf, formatDateTime } from '../../lib/format'
import { keepingView } from '../../lib/tableAddress'
import type { TableColumn } from '../../lib/tableView'
import { useAddressTableView } from '../../lib/useAddressTableView'
import {
  RATE_CONFIRMATION_STATUSES,
  listRateConfirmations,
  type RateConfirmationStatus,
  type RateConfirmationSummary,
} from './api'
import { money, statusTone } from './describe'
import { UploadBox } from './UploadBox'

/** How often the list asks again while a reading is on its way — a reading takes seconds. */
export const REFRESH_MS = 3_000

/** The status the address names, when it names one this build knows; anything else asks for all. */
function statusFrom(params: URLSearchParams): RateConfirmationStatus | undefined {
  const named = params.get('status')
  return RATE_CONFIRMATION_STATUSES.find((status) => status === named)
}

/**
 * What the columns sort and filter by (TRKC-110). The status is filtered from the bar above —
 * the server's filter, before the list's ceiling — so its heading sorts and its box stays empty.
 */
function columnsFor(t: TFunction): TableColumn<RateConfirmationSummary>[] {
  return [
    {
      key: 'received',
      sortValue: (row) => row.receivedAt,
      filter: { kind: 'onOrAfter', value: (row) => businessDayOf(row.receivedAt) },
    },
    { key: 'file', sortValue: (row) => row.fileName, filter: { kind: 'text', text: (row) => row.fileName } },
    { key: 'status', sortValue: (row) => t(`rateConfirmations.statuses.${row.status}`) },
    { key: 'broker', sortValue: (row) => row.brokerName, filter: { kind: 'text', text: (row) => row.brokerName } },
    { key: 'loadNumber', sortValue: (row) => row.loadNumber, filter: { kind: 'text', text: (row) => row.loadNumber } },
    {
      key: 'total',
      sortValue: (row) => row.total,
      filter: { kind: 'atLeast', value: (row) => row.total, keepUnknown: false },
    },
  ]
}

/** The heading each column's filter box is named after. */
const HEADINGS: Record<string, string> = {
  received: 'rateConfirmations.columns.received',
  file: 'rateConfirmations.columns.file',
  status: 'rateConfirmations.columns.status',
  broker: 'rateConfirmations.columns.broker',
  loadNumber: 'rateConfirmations.columns.loadNumber',
  total: 'rateConfirmations.columns.total',
}

const NO_ROWS: RateConfirmationSummary[] = []

/**
 * The rate confirmations, newest first (Stage 3, 3.1): drop a PDF and watch it read. A row
 * shows the file, where its reading stands, and the first rate confirmation's broker, load
 * number and total — the reading itself is one click away. While a reading is on its way the
 * list asks again every few seconds, and stops the moment none is.
 */
export function RateConfirmationsPage() {
  const { t, i18n } = useTranslation()
  const language = i18n.language

  // The status answers from state the page owns, with the address as its copy (TRKC-90).
  const [searchParams, setSearchParams] = useSearchParams()
  const addressStatus = statusFrom(searchParams)
  const [status, setStatus] = useState(addressStatus)
  useEffect(() => setStatus(addressStatus), [addressStatus])

  const [rows, setRows] = useState<RateConfirmationSummary[] | null>(null)
  const [failure, setFailure] = useState<unknown>(null)
  const [reading, setReading] = useState(0)
  // Every reading that finished, failed ones included — what the next one waits on.
  const [settled, setSettled] = useState(0)

  const columns = useMemo(() => columnsFor(t), [t])
  const view = useAddressTableView(rows ?? NO_ROWS, columns)

  // Only the latest request writes: a slower answer to a status the bar has left never
  // replaces the list it describes (GPT-5.6 Sol on #32).
  useEffect(() => {
    let active = true

    listRateConfirmations(status)
      .then((found) => {
        if (active) {
          setRows(found)
          setFailure(null)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setFailure(caught)
        }
      })
      .finally(() => {
        if (active) {
          setSettled((current) => current + 1)
        }
      })

    return () => {
      active = false
    }
  }, [status, reading])

  // A reading on its way: ask again after every answer — a failed one included, or one
  // failure would stop the list for good (Copilot on #44) — and stop once none is reading.
  const inFlight = rows?.some((row) => row.status === 'Reading') ?? false
  useEffect(() => {
    if (!inFlight) {
      return
    }

    const timer = window.setTimeout(() => setReading((current) => current + 1), REFRESH_MS)
    return () => window.clearTimeout(timer)
  }, [inFlight, settled])

  function narrow(next: RateConfirmationStatus | undefined) {
    setStatus(next)
    const params = new URLSearchParams()
    if (next !== undefined) {
      params.set('status', next)
    }
    // The column view the address holds goes along (TRKC-110).
    setSearchParams((current) => keepingView(params, current), { replace: true })
  }

  /**
   * A file just dropped in is shown arriving: it is being read, so a status filter that hides
   * reading files would keep it off the list — and with no row reading, the list would stop
   * asking and never show it read (GPT-6.1 Sol on #66). Such a filter goes back to "any
   * status", in the bar and the address, where the change is in plain sight.
   */
  function uploaded() {
    if (status !== undefined && status !== 'Reading') {
      narrow(undefined)
    } else {
      setReading((current) => current + 1)
    }
  }

  return (
    <section className="page">
      <header className="page__header">
        <h1>{t('rateConfirmations.title')}</h1>
        <p className="page__subtitle">{t('rateConfirmations.subtitle')}</p>
      </header>

      <UploadBox onUploaded={uploaded} />

      <div className="toolbar toolbar--filters">
        <FormSelect
          name="statusFilter"
          label={t('rateConfirmations.filters.status')}
          value={status ?? ''}
          onChange={(value) => narrow(RATE_CONFIRMATION_STATUSES.find((each) => each === value))}
          options={[
            { value: '', label: t('rateConfirmations.filters.anyStatus') },
            ...RATE_CONFIRMATION_STATUSES.map((each) => ({ value: each, label: t(`rateConfirmations.statuses.${each}`) })),
          ]}
        />
      </div>

      {failure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(failure, t, rows === null ? 'rateConfirmations.loadError' : 'rateConfirmations.refreshError')}
        </p>
      )}

      {rows === null ? (
        failure === null && <p className="placeholder">{t('rateConfirmations.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="placeholder">
          {status === undefined ? t('rateConfirmations.empty') : t('rateConfirmations.filters.noneMatch')}
        </p>
      ) : (
        <>
          {inFlight && (
            <p className="muted" role="status">
              {t('rateConfirmations.stillReading')}
            </p>
          )}
          <TableScroll label={t('rateConfirmations.title')}>
            <table className="table">
              <caption className="visually-hidden">{t('rateConfirmations.title')}</caption>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <SortableHeader key={column.key} view={view} column={column.key}>
                      {t(HEADINGS[column.key] ?? column.key)}
                    </SortableHeader>
                  ))}
                </tr>
                <tr className="table__filters">
                  {columns.map((column) => (
                    <FilterCell key={column.key} view={view} column={column} label={t(HEADINGS[column.key] ?? column.key)} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="placeholder">
                      {t('table.noneMatch')}
                    </td>
                  </tr>
                )}
                {view.rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {formatDateTime(row.receivedAt, language)}
                      {row.receivedByEmail !== null && (
                        <>
                          <br />
                          <span className="muted">{row.receivedByEmail}</span>
                        </>
                      )}
                    </td>
                    <td className="rc-list__file">
                      <Link to={`/rate-confirmations/${row.id}`}>{row.fileName}</Link>
                      {row.documents !== null && row.documents > 1 && (
                        <>
                          <br />
                          <span className="muted">{t('rateConfirmations.documentsInFile', { count: row.documents })}</span>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge--${statusTone(row.status)}`}>
                        {t(`rateConfirmations.statuses.${row.status}`)}
                      </span>
                      {row.failure !== null && (
                        <>
                          <br />
                          <span className="muted">{t(`rateConfirmations.outcomesShort.${row.failure}`)}</span>
                        </>
                      )}
                    </td>
                    <td>{row.brokerName ?? <span className="muted">—</span>}</td>
                    <td>{row.loadNumber ?? <span className="muted">—</span>}</td>
                    <td className="table__numeric">
                      {row.total === null ? <span className="muted">—</span> : money(row.total, row.currency, language)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <FilterSummary view={view} />
        </>
      )}
    </section>
  )
}
